
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import {
  Form,
} from '@/components/ui/form';
import { DialogFooter } from '@/components/ui/dialog';
import { useCreateAuthor } from '@/hooks/useCreateAuthor';
import { PAYMENT_METHODS } from '@/utils/constants';
import { addAuthorSchema, FormData } from './AddAuthorForm/formSchema';
import AuthorBasicInfo from './AddAuthorForm/AuthorBasicInfo';
import PaymentMethodSection from './AddAuthorForm/PaymentMethodSection';
import PasswordGenerator from './PasswordGenerator';

interface AddAuthorFormProps {
  onSuccess?: () => void;
}

export default function AddAuthorForm({ onSuccess }: AddAuthorFormProps) {
  const createAuthor = useCreateAuthor();
  const [generatedPassword, setGeneratedPassword] = useState('');
  
  const form = useForm<FormData>({
    resolver: zodResolver(addAuthorSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
      taxPercentage: 10,
      paymentMethod: {
        type: 'paypal',
        threshold: 0,
      },
      paymentDetails: {
        paypalEmail: '',
        bankName: '',
        accountNumber: '',
        routingNumber: '',
        swift: '',
        address: '',
      },
    },
  });

  const watchedPaymentMethod = form.watch('paymentMethod.type');

  // Get threshold based on payment method
  const getThresholdForPaymentMethod = (paymentMethod: string) => {
    const method = Object.values(PAYMENT_METHODS).find(m => m.type === paymentMethod);
    return method ? method.threshold : 0;
  };

  const onSubmit = async (data: FormData) => {
    try {
      // Get the threshold from the payment method constants
      const threshold = getThresholdForPaymentMethod(data.paymentMethod.type);

      await createAuthor.mutateAsync({
        name: data.name,
        email: data.email,
        phone: data.phone,
        address: data.address,
        taxPercentage: data.taxPercentage,
        password: generatedPassword, // Pass the generated password
        paymentMethod: {
          type: data.paymentMethod.type,
          threshold,
        },
        paymentDetails: data.paymentDetails,
      });
      
      form.reset();
      setGeneratedPassword(''); // Reset password
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error creating author:', error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 max-h-[70vh] overflow-y-auto">
        <AuthorBasicInfo form={form} />
        
        <div className="border-t pt-4">
          <PasswordGenerator 
            onPasswordChange={setGeneratedPassword}
          />
        </div>
        
        <PaymentMethodSection form={form} />
        
        <DialogFooter>
          <Button 
            type="submit" 
            disabled={createAuthor.isPending || !generatedPassword}
            className="bg-royalty-600 hover:bg-royalty-700"
          >
            {createAuthor.isPending ? 'Creating...' : 'Add Author'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
